import {
  AdgroupFormData,
  BudgetMode,
  CampaignFormData,
  ScheduleType,
  FULL_TIME_FORMAT,
  SplitTestType,
  CreateMode,
  AdgroupConfig,
} from '@tt4b/ttam-enums';
import {
  useStdAdgroupIs,
  useStdAccountStore,
  useStore,
  useAdgroupMode,
  useStdAdgroupCbo,
  useAllowlist,
} from '@tt4b/ttam-hooks';
import { Ref, ref, computed } from '@vue/composition-api';
import { dayjs, convertGMT2UTC, isDST, createLogger } from '@tt4b/ttam-utils';
import { i18n } from '@tt4b/ttam-i18n';

const logger = createLogger('AdgroupSchedule');

export function useContinuousRadioPropsDisabled(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isSplitTestEnabled?: Ref<boolean>;
}) {
  const { adgroupData, campaignData, isSplitTestEnabled = ref(false) } = props;
  const { isSpc } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return {
    disabled: computed(() => {
      const budgetMode = isCBO.value && !isSpc.value ? campaignData.value.budgetMode : adgroupData.value.budgetMode;
      return budgetMode === BudgetMode.TOTAL_BUDGET || isSplitTestEnabled.value;
    }),
  };
}

export function useContinuousTimepickerPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData } = props;
  return {
    visible: computed(
      () =>
        // TODO 待splittest支持
        // if (isSplitTestEnabled.value) {
        //   return false;
        // }

        adgroupData?.value?.scheduleType === ScheduleType.CONTINOUSLY,
    ),
  };
}

export function useDateDurationRadioPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isLiveAds, isCILiveAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isLiveAdsScheduleDurationWhite } = useAllowlist();
  const visible = computed(() => (isLiveAds.value && isLiveAdsScheduleDurationWhite.value) || isCILiveAds.value);
  return {
    visible,
  };
}

export function useDaterangeRadioPropsDisabled(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isSplitTestEnabled?: Ref<boolean>;
}) {
  const { adgroupData, campaignData, isSplitTestEnabled = ref(false) } = props;
  const { isSpc } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { visible: isShowDateDurationRadio } = useDateDurationRadioPropsVisible(props);

  const budgetMode = computed(() =>
    isCBO.value && !isSpc.value ? campaignData.value.budgetMode : adgroupData.value.budgetMode,
  );
  const isDaterangeRadioDisabledByBudgetMode = computed(() =>
    isShowDateDurationRadio.value
      ? false
      : ![BudgetMode.DYNAMIC_DAILY_BUDGET, BudgetMode.DAILY_BUDGET].includes(budgetMode.value),
  );
  return {
    disabled: computed(() => isDaterangeRadioDisabledByBudgetMode.value || isSplitTestEnabled.value),
  };
}

export function useDaterangeTimepickerPropsStartTimeDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  const { adgroupConfig } = props;
  const { disableStartTime } = adgroupConfig.value;

  return {
    startTimeDisabled: computed(() => disableStartTime),
  };
}

export function useDatedurationTimepickerPropsDurationDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  const { adgroupConfig } = props;

  return {
    durationTimeDisabled: computed(() => adgroupConfig.value.disableDurationTime),
  };
}

export function useDaterangeRadioPropsLabel() {
  const label = i18n.t('module_adgroup_date_range_option_label') as string;

  return {
    label,
  };
}

export function useDaterangeRadioPropsHint(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isLiveAds, isCILiveAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const hint = computed(() => {
    if (isLiveAds.value || isCILiveAds.value) {
      return i18n.t('module_adgroup_schedule_type_continuously_hover_hint') as string;
    }
    return '';
  });
  return {
    hint,
  };
}

export function useDateDurationTimepickerPropsVisible(props: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = props;
  return {
    visible: computed(() => adgroupData.value.scheduleType === ScheduleType.DATE_DURATION),
  };
}

export function useShopliveTipsPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isLiveAds } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  return {
    visible: computed(() => isLiveAds.value),
  };
}

/**
 * live ads 夏令时展示tips
 * @param props
 * @returns
 */
export function useDstTipsPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isLiveAds } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const { state } = useStore();

  const visible = computed(() => {
    const areaMap = state.areaData.data.value?.areaMap;
    const timezoneList = state.timezoneList.data.value?.timezone_list ?? [];
    const countryTimezoneList =
      adgroupData.value.country?.map?.(id => convertGMT2UTC(areaMap?.[id]?.delivery_timezone)) ?? [];
    const liveAdsTimezoneList =
      timezoneList?.filter(tz => countryTimezoneList.includes(convertGMT2UTC(tz.gmt_offset))).map(tz => tz.timezone) ??
      [];

    return isLiveAds.value && liveAdsTimezoneList?.some?.(tz => isDST(adgroupData.value.startTime, tz));
  });

  return {
    visible,
  };
}

export function useDaterangeTimepickerPropsEndTimeDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  const { adgroupConfig } = props;

  return {
    endTimeDisabled: computed(() => adgroupConfig.value.disableEndTime),
  };
}

export function useDaterangeTimepickerPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData } = props;
  return {
    visible: computed(
      () =>
        // TODO 待splittest支持
        // if (isSplitTestEnabled.value) {
        //   return true;
        // }

        adgroupData?.value?.scheduleType === ScheduleType.DATE_RANGE,
    ),
  };
}

/** 初始值获取  */
export function useInitDataGet(props: { timezone?: Ref<string>; campaignData: Ref<CampaignFormData> }) {
  const { timezone, campaignData } = props;

  const { timezone: alternativeTimezone } = useStdAccountStore();

  const _timezoneValue = timezone?.value ?? alternativeTimezone?.value ?? '';

  const isSplitTestEnabled = computed(() => campaignData.value?.abTest !== SplitTestType.NONE);

  const nowTime = (dayjs as any).tz(Date.now(), _timezoneValue || '');
  const startTime: string = isSplitTestEnabled.value
    ? nowTime.add(6, 'hour').format(FULL_TIME_FORMAT)
    : nowTime.format(FULL_TIME_FORMAT);
  const endTime: string = isSplitTestEnabled.value
    ? nowTime.add(7, 'day').add(6, 'hour').format(FULL_TIME_FORMAT)
    : nowTime.add(2, 'week').format(FULL_TIME_FORMAT);

  logger.info(
    'useInitDataGet: %s',
    JSON.stringify({
      alternativeTimezone: alternativeTimezone.value,
      _timezoneValue,
      nowTime,
      startTime,
      endTime,
    }),
  );

  const scheduleType = isSplitTestEnabled.value ? ScheduleType.DATE_RANGE : ScheduleType.CONTINOUSLY;

  return {
    scheduleType,
    startTime,
    endTime,
  };
}
